// The WebGPU context: the device, the registry, the units table and the
// frame (the runtime design §1, §3, §5, §6, §7).
//
// It is the WebGPU half of `gfx/types.ts` `FrameGfx`, and it mirrors
// `gl/context.ts` one to one. The engine and every layer see the interface;
// this file is the only one that holds the device and the canvas context, and
// the sizing is the `gfx/surface.ts` both backends share.
//
// Four rules live here and nowhere else:
//
//  1. **The canvas is claimed LAST.** A canvas that gave a `webgpu` context
//     refuses `webgl2` after it, so the device is asked for first
//     (`gfx/select.ts`) and the registry and the caches are built before the
//     canvas is touched at all.
//  2. **One blend state.** `src: one, dst: one-minus-src-alpha` with
//     premultiplied output, as WebGL2 sets it. WebGPU carries blend on the
//     pipeline, so the flag rides the frame and `pipeline.ts` keys its cache
//     by it.
//  3. **Every GPU object is a `GpuResource`.** A lost device destroys them
//     all; the restore recreates them in the order they were registered, so
//     a batch that needs its corner buffer finds the buffer already there.
//     The list is the `gfx/registry.ts` both backends share (R1369).
//  4. **A texture binds into a TABLE, not into the pipeline.** `bind(unit)`
//     writes the view into `units[unit]`, and the draw builds the group from
//     the table. That keeps `WorldPass` and the three batches unchanged.

import { Surface, hasQueryFlag } from '../gfx/surface';
import { readQueryKnobs, type QueryKnobs } from '../knobs';
import { GpuTimestampRing } from '../../perf/gputimer-gpu';
import { navigatorGpu, requestGpu, type GpuBundle } from './device';
import { UniformArena } from './arena';
import { BindGroups, type UnitSlot } from './bindgroups';
import { GpuFrame, type DrawnProbe } from './frame';
import { TEXTURE_UNITS } from '../shaders/wgsl/bindings';
import { ResourceRegistry, type GfxResource } from '../gfx/registry';
import type { DrawnSize, FrameGfx } from '../gfx/types';

/**
 * One GPU object the context owns: the shared resource shape over the
 * device (R1369).
 *
 * `create` builds the object on the live device; `destroy` drops the handles.
 * Both must be safe to call twice. A WebGPU resource declares `destroy()`
 * with no device and ignores the one the registry passes, because a WebGPU
 * object destroys itself and the device it belonged to may be gone.
 */
export type GpuResource = GfxResource<GPUDevice>;

/** A GPU object that can be destroyed: a texture, a buffer, a query set. */
export interface GpuRetirable {
  destroy(): void;
}

/**
 * What a draw needs from the pipeline that is in use.
 *
 * The instance buffer and the mesh buffer draw with whatever `use()` bound
 * last, exactly as they do on WebGL2. The interface is stated here rather
 * than imported, so `pipeline.ts` can import this file and not the other way.
 */
export interface DrawBinding {
  /**
   * Sets the pipeline, pushes the uniform block and binds both groups.
   * Answers the open pass, or null where the draw must be dropped.
   */
  bindDraw(): GPURenderPassEncoder | null;
}

/** The context wrapper every module of the WebGPU backend is handed. */
export interface Gpu extends FrameGfx {
  readonly backend: 'webgpu';
  readonly device: GPUDevice;
  readonly queue: GPUQueue;
  readonly frame: GpuFrame;
  readonly arena: UniformArena;
  readonly groups: BindGroups;
  /** The 9 texture units, as `bind(unit)` writes them. */
  readonly units: readonly UnitSlot[];
  /** The pipeline `use()` bound last. A buffer draws with it. */
  current: DrawBinding | null;
  register(r: GpuResource): void;
  unregister(r: GpuResource): void;
  /**
   * Destroys a GPU object that the open frame may still use (R1340).
   *
   * A command of the open frame may name a texture or a buffer, and the
   * submit fails validation when that object is destroyed first. So inside
   * an open frame the object waits in a queue that `endFrame` empties after
   * `queue.submit`. Outside a frame it is destroyed at once.
   */
  retire(obj: GpuRetirable): void;
  /** A fresh id for one GPU texture. It keys the group-1 cache. */
  textureId(): number;
}

/** How the canvas is configured. Opaque, because the background is opaque. */
const CANVAS_ALPHA_MODE: GPUCanvasAlphaMode = 'opaque';

/**
 * The format a canvas falls back to where the browser states none.
 *
 * The device compile check of `dev/wgsl-check.html` reads it too, so it
 * builds its pipelines against the format the runtime draws into.
 */
export const FALLBACK_CANVAS_FORMAT: GPUTextureFormat = 'bgra8unorm';

/**
 * The timestamp ring as a `GpuResource` (R431).
 *
 * The ring lives in `perf/gputimer-gpu.ts`, because `perf/` reports the same
 * column for both backends and imports nothing from the engine. This adapter
 * is the whole of what the engine adds: it rebuilds the ring after a restore,
 * because a query set belongs to the device that made it.
 */
class TimerResource implements GpuResource {
  ring: GpuTimestampRing | null = null;

  constructor(private readonly wanted: boolean) {}

  create(device: GPUDevice): void {
    this.destroy();
    if (!this.wanted) return;
    this.ring = new GpuTimestampRing(device);
  }

  destroy(): void {
    this.ring?.destroy();
    this.ring = null;
  }
}

class GpuContext implements Gpu {
  readonly backend = 'webgpu' as const;
  readonly glcheck: boolean;
  /** `?aa=1` has no WebGPU meaning: the engine antialiases in the shader. */
  readonly aa = false;
  readonly renderer: string;
  readonly arena = new UniformArena();
  readonly units: UnitSlot[] = Array.from(
    { length: TEXTURE_UNITS }, () => ({ view: null, id: 0 }));
  readonly groups = new BindGroups(this.arena, this.units);
  /** Both follow the DEVICE, so a restore replaces them with the new one. */
  queue: GPUQueue;
  frame: GpuFrame;

  lost = false;
  drawCalls = 0;
  instances = 0;
  current: DrawBinding | null = null;

  device: GPUDevice;

  private readonly surface: Surface;
  private readonly canvasContext: GPUCanvasContext;
  private readonly canvasFormat: GPUTextureFormat;
  /** The size the last frame drew into, which a restore's new frame keeps (R1498). */
  private readonly drawn: DrawnProbe = { w: 0, h: 0, on: null };
  private readonly timer: TimerResource;
  /**
   * A WebGPU object destroys itself whatever its device, so `unregister` and
   * `dispose` destroy a resource while the device is lost too.
   */
  private readonly registry = new ResourceRegistry<GPUDevice>({
    handle: () => this.device,
    lost: () => this.lost,
    destroyWhileLost: true,
  });
  /** What `retire` holds until the open frame is submitted (R1340). */
  private readonly retired: GpuRetirable[] = [];
  private nextId = 1;
  /** How many validation scopes `check` has open. `endFrame` closes them. */
  private scopes = 0;
  /** An uncaptured error is logged once a frame, not once a draw. */
  private saidThisFrame = false;
  private restoring = false;
  private disposed = false;

  constructor(
    readonly canvas: HTMLCanvasElement,
    bundle: GpuBundle,
    query?: QueryKnobs,
  ) {
    const knobs = query ?? readQueryKnobs();
    this.device = bundle.device;
    this.queue = bundle.device.queue;
    this.renderer = bundle.info;
    this.glcheck = hasQueryFlag('glcheck');

    // The timer registers FIRST, so a restore rebuilds it before any program.
    this.timer = new TimerResource(bundle.timestamps);
    this.register(this.timer);
    this.arena.create(this.device);
    this.groups.create(this.device);
    this.watchDevice(this.device);

    // The canvas is claimed LAST. Everything above it is device work; this
    // line is the one that makes the canvas a WebGPU canvas for good.
    this.canvasFormat = navigatorGpu()?.getPreferredCanvasFormat() ?? FALLBACK_CANVAS_FORMAT;
    // The cast is the library's gap and not a shortcut: the DOM library this
    // project compiles against declares no `webgpu` overload of `getContext`.
    const ctx = canvas.getContext('webgpu') as GPUCanvasContext | null;
    if (!ctx) throw new Error('fast: the canvas gives no WebGPU context');
    this.canvasContext = ctx;
    this.frame = this.claim(this.device);
    this.surface = new Surface(canvas, knobs.dpr ?? undefined);
  }

  /** Points the canvas at one device, and builds the frame over the two. */
  private claim(device: GPUDevice): GpuFrame {
    this.canvasContext.configure({
      device, format: this.canvasFormat, alphaMode: CANVAS_ALPHA_MODE,
    });
    return new GpuFrame(device, {
      canvasContext: this.canvasContext,
      canvasFormat: this.canvasFormat,
      arena: this.arena,
      timer: () => this.timer.ring,
      pixelSize: () => [this.pixelWidth, this.pixelHeight],
      drawn: this.drawn,
    });
  }

  // -- size ------------------------------------------------------------------

  get width(): number { return this.surface.width; }
  get height(): number { return this.surface.height; }
  get pixelWidth(): number { return this.surface.pixelWidth; }
  get pixelHeight(): number { return this.surface.pixelHeight; }
  get dpr(): number { return this.surface.dpr; }

  onResize(fn: () => void) { this.surface.onResize(fn); }
  onRestore(fn: () => void) { this.registry.onRestore(fn); }

  /** The canvas texture of the last watched frame (R1498). */
  drawnSize(): DrawnSize { return { what: 'tex', w: this.drawn.w, h: this.drawn.h }; }
  watchDrawn(fn: () => void): void { this.drawn.on = fn; }

  textureId(): number { return this.nextId++; }

  // -- the frame -------------------------------------------------------------

  beginFrame(clear: boolean, clearRgb: readonly [number, number, number]): void {
    if (this.lost) return;
    this.saidThisFrame = false;
    this.frame.begin(clear, clearRgb);
    if (this.glcheck) {
      this.device.pushErrorScope('validation');
      this.scopes++;
    }
  }

  endFrame(): void {
    if (this.lost) return;
    this.closeScopes('the frame');
    this.frame.end();
    this.current = null;
    this.flushRetired();
  }

  retire(obj: GpuRetirable): void {
    if (this.frame.open) this.retired.push(obj);
    else obj.destroy();
  }

  /** Destroys what `retire` held. The frame that used it is submitted or dropped. */
  private flushRetired(): void {
    const q = this.retired;
    for (let i = 0; i < q.length; i++) {
      try { q[i].destroy(); } catch { /* the device went away with it */ }
    }
    q.length = 0;
  }

  /**
   * Turns the one blend state off for one pass, and on again after it.
   *
   * WebGPU carries blend on the pipeline, so this writes a flag and the
   * pipeline cache builds the second variant on demand.
   */
  setBlend(on: boolean): void { this.frame.blend = on; }

  get gpuMs(): number | null { return this.timer.ring?.lastMs ?? null; }

  // -- resources -------------------------------------------------------------

  register(r: GpuResource) { this.registry.register(r); }
  unregister(r: GpuResource) { this.registry.unregister(r); }

  // -- loss and restore ------------------------------------------------------

  /**
   * Watches one device.
   *
   * A device that is DESTROYED was destroyed by `loseContext()`, so it does
   * not come back by itself: `restoreContext()` is the other half of the
   * recovery test. Every other reason is the driver's, and the context asks
   * for a new device at once (the runtime design §7).
   */
  private watchDevice(device: GPUDevice): void {
    device.addEventListener?.('uncapturederror', this.onUncaptured);
    void device.lost.then((info) => {
      if (this.device !== device || this.disposed) return;
      this.lost = true;
      this.frame.abandon();
      this.flushRetired();
      this.current = null;
      this.scopes = 0;
      this.registry.destroyAll();
      this.arena.destroy();
      this.groups.destroy();
      if (info.reason !== 'destroyed') void this.restore();
    });
  }

  private onUncaptured = (e: Event) => {
    if (this.saidThisFrame) return;
    this.saidThisFrame = true;
    const err = (e as GPUUncapturedErrorEvent).error;
    // eslint-disable-next-line no-console
    console.error('fast: uncaptured GPU error', err?.message ?? err);
  };

  /**
   * Asks for a new device and rebuilds everything on it.
   *
   * The canvas keeps the context it has; only the device changes, so the
   * configure call points the same canvas at the new one. A restore that
   * fails leaves `lost` true: the loop then draws nothing and the stats line
   * says so, and there is no fall back to WebGL2 after boot.
   */
  private async restore(): Promise<void> {
    if (this.restoring || this.disposed) return;
    this.restoring = true;
    try {
      const bundle = await requestGpu(navigatorGpu());
      if (!bundle || this.disposed) return;
      this.device = bundle.device;
      this.queue = bundle.device.queue;
      this.watchDevice(bundle.device);
      this.frame = this.claim(bundle.device);
      this.arena.create(bundle.device);
      this.groups.invalidate();
      this.groups.create(bundle.device);
      this.lost = false;
      // Registration order is dependency order: a batch registered after the
      // corner buffer finds the buffer already rebuilt. `this.device` is the
      // new device here, so the registry builds on it.
      this.registry.restore();
    } finally {
      this.restoring = false;
    }
  }

  // -- diagnostics -----------------------------------------------------------

  /**
   * Reads the validation state under `?glcheck`.
   *
   * WebGL2 asks the driver for one error code. WebGPU has no such call: it
   * has SCOPES, so the check is rolling — it closes the scope the last check
   * opened, reports what that scope caught, and opens the next one. The first
   * scope opens in `beginFrame` and the last one closes in `endFrame`.
   */
  check(where: string) {
    if (!this.glcheck || this.lost) return;
    this.closeScopes(where);
    this.device.pushErrorScope('validation');
    this.scopes++;
  }

  private closeScopes(where: string): void {
    while (this.scopes > 0) {
      this.scopes--;
      void this.device.popErrorScope().then((err) => {
        if (!err) return;
        // eslint-disable-next-line no-console
        console.error(`fast: GPU error after ${where}`, err.message);
      }).catch(() => { /* the device went away with the scope */ });
    }
  }

  /** Destroys the device. The reason is `destroyed`, so nothing auto-restores. */
  loseContext() { this.device.destroy(); }

  restoreContext() { void this.restore(); }

  dispose() {
    if (this.disposed) return;
    this.disposed = true;
    this.surface.dispose();
    this.frame.abandon();
    this.flushRetired();
    this.registry.dispose();
    this.arena.destroy();
    this.groups.destroy();
    this.device.destroy();
  }
}

/** Builds the one WebGPU context of the fast client. */
export function createGpuContext(
  canvas: HTMLCanvasElement, bundle: GpuBundle, knobs?: QueryKnobs,
): Gpu {
  return new GpuContext(canvas, bundle, knobs);
}
